import React, { useEffect, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Save, CreditCard, Building2, BrainCircuit, Stethoscope, FileCheck2 } from 'lucide-react';

const inputCls = 'w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

export default function GlobalIntegrationsPage() {
  const [items, setItems] = useState([]);
  const [px, setPx] = useState(null);
  const [pxForm, setPxForm] = useState({ instance: '', api_secret: '', webhook_secret: '' });
  const [stripeStatus, setStripeStatus] = useState(null);
  const [savingPx, setSavingPx] = useState(false);
  const [zx, setZx] = useState(null);
  const [zxForm, setZxForm] = useState({ username: '', password: '' });
  const [savingZx, setSavingZx] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiForm, setAiForm] = useState({ primary_provider: 'openai', openai_key: '', anthropic_key: '', google_key: '', fallback_provider: '', fallback_model: '', service_models: {} });
  const [savingAi, setSavingAi] = useState(false);
  const [testingAi, setTestingAi] = useState(false);
  const [aiTest, setAiTest] = useState(null);
  const [rd, setRd] = useState(null);
  const [rdForm, setRdForm] = useState({ api_key: '' });
  const [savingRd, setSavingRd] = useState(false);
  const [md, setMd] = useState(null);
  const [mdForm, setMdForm] = useState({ enabled: false, environment: 'acc', acc_base_url: '', acc_client_id: '', acc_cert_pem: '', acc_key_pem: '', prod_base_url: '', prod_client_id: '', prod_cert_pem: '', prod_key_pem: '', master_id: '', timeout_s: 30 });
  const [savingMd, setSavingMd] = useState(false);
  const [testingMd, setTestingMd] = useState(false);

  const loadPx = () => {
    superAdminApi.getPlatformPayrexx().then((r) => { setPx(r.data); setPxForm({ instance: r.data.instance || '', api_secret: '', webhook_secret: '' }); }).catch(() => {});
    superAdminApi.getPlatformStripe().then((r) => setStripeStatus(r.data)).catch(() => {});
    superAdminApi.getPlatformZefix().then((r) => { setZx(r.data); setZxForm({ username: r.data.username || '', password: '' }); }).catch(() => {});
    superAdminApi.getPlatformAi().then((r) => {
      setAi(r.data);
      const sm = {};
      (r.data.features || []).forEach((f) => { sm[f.id] = { provider: f.provider || '', model: f.model || '' }; });
      setAiForm({ primary_provider: r.data.primary_provider || 'openai', openai_key: '', anthropic_key: '', google_key: '', fallback_provider: r.data.fallback_provider || '', fallback_model: r.data.fallback_model || '', service_models: sm });
    }).catch(() => {});
    superAdminApi.getPlatformRefdata().then((r) => { setRd(r.data); setRdForm({ api_key: '' }); }).catch(() => {});
    superAdminApi.getPlatformMedidata().then((r) => { setMd(r.data); setMdForm({ enabled: !!r.data.enabled, environment: r.data.environment || 'acc', acc_base_url: r.data.acc_base_url || '', acc_client_id: r.data.acc_client_id || '', acc_cert_pem: '', acc_key_pem: '', prod_base_url: r.data.prod_base_url || '', prod_client_id: r.data.prod_client_id || '', prod_cert_pem: '', prod_key_pem: '', master_id: r.data.master_id || '', timeout_s: r.data.timeout_s || 30 }); }).catch(() => {});
  };

  useEffect(() => {
    superAdminApi.integrations().then((r) => setItems(r.data.integrations || [])).catch(() => {});
    loadPx();
  }, []);

  const savePx = async () => {
    setSavingPx(true);
    try {
      const payload = { instance: pxForm.instance };
      if (pxForm.api_secret) payload.api_secret = pxForm.api_secret;
      if (pxForm.webhook_secret) payload.webhook_secret = pxForm.webhook_secret;
      await superAdminApi.savePlatformPayrexx(payload);
      toast.success('Payrexx-Zugang (InvictaX) gespeichert');
      loadPx();
      superAdminApi.integrations().then((r) => setItems(r.data.integrations || []));
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSavingPx(false); }
  };

  const saveZx = async () => {
    setSavingZx(true);
    try {
      const payload = { username: zxForm.username };
      if (zxForm.password) payload.password = zxForm.password;
      await superAdminApi.savePlatformZefix(payload);
      toast.success('Zefix-Zugang gespeichert');
      loadPx();
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSavingZx(false); }
  };

  const saveAi = async () => {
    setSavingAi(true);
    try {
      const payload = { primary_provider: aiForm.primary_provider, enabled: true, fallback_provider: aiForm.fallback_provider || '', fallback_model: aiForm.fallback_model || '', service_models: aiForm.service_models || {} };
      if (aiForm.openai_key) payload.openai_key = aiForm.openai_key;
      if (aiForm.anthropic_key) payload.anthropic_key = aiForm.anthropic_key;
      if (aiForm.google_key) payload.google_key = aiForm.google_key;
      await superAdminApi.savePlatformAi(payload);
      toast.success('Plattform-KI-Keys gespeichert');
      loadPx();
      superAdminApi.integrations().then((r) => setItems(r.data.integrations || []));
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSavingAi(false); }
  };

  const testAi = async () => {
    setTestingAi(true);
    setAiTest(null);
    try {
      const r = await superAdminApi.testPlatformAi();
      setAiTest(r.data);
      if (r.data?.ok) toast.success('KI-Test erfolgreich');
      else toast.error('KI-Test: Problem erkannt');
    } catch { toast.error('KI-Test fehlgeschlagen'); }
    finally { setTestingAi(false); }
  };

  const saveRd = async () => {
    setSavingRd(true);
    try {
      const payload = {};
      if (rdForm.api_key) payload.api_key = rdForm.api_key;
      await superAdminApi.savePlatformRefdata(payload);
      toast.success('Refdata-Key gespeichert');
      loadPx();
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSavingRd(false); }
  };

  const readMdFile = (file, field) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setMdForm((p) => ({ ...p, [field]: String(r.result || '') }));
    r.readAsText(file);
  };

  const saveMd = async () => {
    setSavingMd(true);
    try {
      await superAdminApi.savePlatformMedidata(mdForm);
      toast.success('MediData gespeichert');
      loadPx();
    } catch { toast.error('Speichern fehlgeschlagen'); }
    finally { setSavingMd(false); }
  };

  const testMd = async () => {
    setTestingMd(true);
    try {
      const r = await superAdminApi.testPlatformMedidata();
      if (r.data?.ok) toast.success('MediData-Verbindung OK');
      else toast.error(`MediData-Test: ${r.data?.message || r.data?.error || 'simuliert/unvollständig'}`);
    } catch { toast.error('MediData-Test fehlgeschlagen'); }
    finally { setTestingMd(false); }
  };

  // „google" (UI) entspricht intern dem Anbieter-Schlüssel „gemini".
  const normProv = (p) => (p === 'google' ? 'gemini' : (p || ''));
  // Verfügbare (neuesten) Modelle je Anbieter — automatisch vom Backend geladen.
  const modelsFor = (p) => (ai?.available_models?.[normProv(p)] || []);

  return (
    <div data-testid="sa-integrations" className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Globale Integrationen</h1>
        <p className="mt-1 text-sm text-slate-600">Plattformweite Zugänge – gelten für alle Mandanten.</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((it) => (
          <div key={it.key} data-testid={`sa-int-status-${it.key}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
            <div>
              <div className="text-sm font-medium text-slate-800">{it.name}</div>
              <div className="text-xs text-slate-500">{it.detail}{it.masked ? ` · ${it.masked}` : ''}</div>
            </div>
            {it.configured
              ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              : <XCircle className="h-5 w-5 text-slate-400" />}
          </div>
        ))}
      </div>

      {/* InvictaX-Zahlungen (Plattform-Einnahmen) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-int-payments">
        <div className="mb-5 flex items-center gap-2">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-700"><CreditCard className="h-4.5 w-4.5" /></div>
          <div>
            <h2 className="text-lg font-medium text-slate-900">InvictaX-Zahlungen (Plattform-Einnahmen)</h2>
            <p className="text-xs text-slate-500">Zugänge, über die KI-Guthaben &amp; Software-Abos an InvictaX eingezogen werden.</p>
          </div>
        </div>

        {/* Stripe-Status */}
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div><div className="text-sm font-medium text-slate-800">Stripe · KI-Guthaben</div><div className="text-xs text-slate-500">Einmalzahlungen (Test)</div></div>
            {stripeStatus?.ai_credit_configured ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-slate-400" />}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div><div className="text-sm font-medium text-slate-800">Stripe · Software-Abo</div><div className="text-xs text-slate-500">Wiederkehrend (Test){stripeStatus?.subscription_account_id ? ` · ${stripeStatus.subscription_account_id}` : ''}</div></div>
            {stripeStatus?.subscription_configured ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-slate-400" />}
          </div>
        </div>

        {/* Payrexx (InvictaX) */}
        <div className="mb-3 text-sm font-medium text-slate-800">Payrexx (InvictaX-Instanz)</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Instanz (Subdomain)</span>
            <input data-testid="sa-px-instance" className={inputCls} placeholder="z.B. invictax" value={pxForm.instance} onChange={(e) => setPxForm({ ...pxForm, instance: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">API-Secret {px?.has_api_secret && <span className="text-slate-500">(gesetzt: {px.api_secret_masked})</span>}</span>
            <input data-testid="sa-px-apisecret" className={inputCls} type="password" placeholder={px?.has_api_secret ? 'Leer lassen = unverändert' : 'API-Secret eingeben'} value={pxForm.api_secret} onChange={(e) => setPxForm({ ...pxForm, api_secret: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Webhook-Secret {px?.has_webhook_secret && <span className="text-slate-500">(gesetzt)</span>}</span>
            <input data-testid="sa-px-webhook" className={inputCls} type="password" placeholder={px?.has_webhook_secret ? 'Leer lassen = unverändert' : 'optional'} value={pxForm.webhook_secret} onChange={(e) => setPxForm({ ...pxForm, webhook_secret: e.target.value })} />
          </label>
        </div>
        <div className="mt-6">
          <button data-testid="sa-save-payrexx-btn" onClick={savePx} disabled={savingPx}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-400 disabled:opacity-50">
            <Save className="h-4 w-4" /> {savingPx ? 'Speichert…' : 'Payrexx speichern'}
          </button>
        </div>
      </div>

      {/* KI / LLM – Plattform-Standard-Key für alle Mandanten */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-int-ai">
        <div className="mb-5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/15 text-violet-700"><BrainCircuit className="h-4.5 w-4.5" /></div>
            <div>
              <h2 className="text-lg font-medium text-slate-900">KI / LLM – Plattform-Standard</h2>
              <p className="text-xs text-slate-500">Zentraler KI-Key für alle Mandanten. Eine Praxis kann in ihren eigenen KI-Einstellungen einen eigenen Key hinterlegen (überschreibt den Plattform-Key).</p>
            </div>
          </div>
          {ai && (ai.has_openai || ai.has_anthropic || ai.has_google)
            ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Aktiv</span>
            : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500"><XCircle className="h-3.5 w-3.5" /> Kein Plattform-Key</span>}
        </div>
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Standard-Anbieter</span>
            <select data-testid="sa-ai-provider" className={inputCls} value={aiForm.primary_provider} onChange={(e) => setAiForm({ ...aiForm, primary_provider: e.target.value })}>
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="gemini">Google (Gemini)</option>
            </select>
          </label>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">OpenAI API-Key {ai?.has_openai && <span className="text-slate-500">(gesetzt: {ai.openai_masked})</span>}</span>
            <input data-testid="sa-ai-openai" className={inputCls} type="password" placeholder={ai?.has_openai ? 'Leer lassen = unverändert' : 'sk-…'} value={aiForm.openai_key} onChange={(e) => setAiForm({ ...aiForm, openai_key: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Anthropic API-Key {ai?.has_anthropic && <span className="text-slate-500">(gesetzt: {ai.anthropic_masked})</span>}</span>
            <input data-testid="sa-ai-anthropic" className={inputCls} type="password" placeholder={ai?.has_anthropic ? 'Leer lassen = unverändert' : 'sk-ant-…'} value={aiForm.anthropic_key} onChange={(e) => setAiForm({ ...aiForm, anthropic_key: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Google API-Key {ai?.has_google && <span className="text-slate-500">(gesetzt: {ai.google_masked})</span>}</span>
            <input data-testid="sa-ai-google" className={inputCls} type="password" placeholder={ai?.has_google ? 'Leer lassen = unverändert' : 'AIza…'} value={aiForm.google_key} onChange={(e) => setAiForm({ ...aiForm, google_key: e.target.value })} />
          </label>
        </div>
        {/* Automatischer Fallback bei Anbieter-Ausfall */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="sa-ai-fallback">
          <p className="text-sm font-medium text-slate-800">Automatischer Fallback</p>
          <p className="mb-3 text-xs text-slate-500">Fällt der Primär-Anbieter aus (laut Gesundheitscheck), schaltet das System automatisch auf diesen Zweit-Anbieter um.</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-600">Fallback-Anbieter</span>
              <select data-testid="sa-ai-fallback-provider" className={inputCls} value={aiForm.fallback_provider}
                onChange={(e) => setAiForm({ ...aiForm, fallback_provider: e.target.value })}>
                <option value="">— kein Fallback —</option>
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google (Gemini)</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-600">Fallback-Modell</span>
              <select data-testid="sa-ai-fallback-model" className={inputCls}
                value={aiForm.fallback_model} onChange={(e) => setAiForm({ ...aiForm, fallback_model: e.target.value })}>
                <option value="">— Standard (empfohlen) —</option>
                {modelsFor(aiForm.fallback_provider).map((m) => (
                  <option key={m} value={m}>{m}{ai?.recommended?.[normProv(aiForm.fallback_provider)] === m ? ' ★' : ''}</option>
                ))}
                {aiForm.fallback_model && !modelsFor(aiForm.fallback_provider).includes(aiForm.fallback_model) && (
                  <option value={aiForm.fallback_model}>{aiForm.fallback_model} (eigen)</option>
                )}
              </select>
            </label>
          </div>
        </div>

        {/* Zentrale KI pro Funktion */}
        <div className="mt-6" data-testid="sa-ai-features">
          <p className="text-sm font-medium text-slate-800">Zentrale KI pro Funktion</p>
          <p className="mb-3 text-xs text-slate-500">Lege pro Funktion einen Standard-Anbieter &amp; ein Modell fest. Leer = Standard-Anbieter oben. Eine Praxis kann dies in ihren KI-Einstellungen überschreiben.</p>
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
            {(ai?.features || []).map((f) => (
              <div key={f.id} data-testid={`sa-ai-feat-${f.id}`} className="grid grid-cols-1 items-center gap-2 px-3 py-2.5 sm:grid-cols-[1.4fr_1fr_1.2fr]">
                <div className="min-w-0">
                  <div className="truncate text-sm text-slate-800">{f.label}</div>
                  <div className="text-[11px] text-slate-400">Standard: {f.default_model || '—'}</div>
                </div>
                <select data-testid={`sa-ai-feat-provider-${f.id}`} className={inputCls}
                  value={(aiForm.service_models?.[f.id]?.provider) || ''}
                  onChange={(e) => setAiForm({ ...aiForm, service_models: { ...aiForm.service_models, [f.id]: { ...(aiForm.service_models?.[f.id] || {}), provider: e.target.value } } })}>
                  <option value="">Standard</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="google">Google</option>
                </select>
                {(() => {
                  const prov = (aiForm.service_models?.[f.id]?.provider) || aiForm.primary_provider;
                  const model = (aiForm.service_models?.[f.id]?.model) || '';
                  const opts = modelsFor(prov);
                  return (
                    <select data-testid={`sa-ai-feat-model-${f.id}`} className={inputCls}
                      value={model}
                      onChange={(e) => setAiForm({ ...aiForm, service_models: { ...aiForm.service_models, [f.id]: { ...(aiForm.service_models?.[f.id] || {}), model: e.target.value } } })}>
                      <option value="">Standard ({f.default_model || '—'})</option>
                      {opts.map((m) => (
                        <option key={m} value={m}>{m}{ai?.recommended?.[normProv(prov)] === m ? ' ★' : ''}</option>
                      ))}
                      {model && !opts.includes(model) && <option value={model}>{model} (eigen)</option>}
                    </select>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button data-testid="sa-save-ai-btn" onClick={saveAi} disabled={savingAi}
            className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-50">
            <Save className="h-4 w-4" /> {savingAi ? 'Speichert…' : 'KI-Keys speichern'}
          </button>
          <button data-testid="sa-test-ai-btn" onClick={testAi} disabled={testingAi}
            className="inline-flex items-center gap-2 rounded-xl border border-violet-300 bg-white px-5 py-2.5 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50">
            <BrainCircuit className="h-4 w-4" /> {testingAi ? 'Teste…' : 'KI testen'}
          </button>
        </div>
        {aiTest && (
          <div data-testid="sa-ai-test-result" className={`mt-4 rounded-xl border p-4 ${aiTest.ok ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {aiTest.ok
                ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /><span className="text-emerald-800">KI erreichbar</span></>
                : <><XCircle className="h-4 w-4 text-rose-600" /><span className="text-rose-800">KI-Problem erkannt</span></>}
            </div>
            {!aiTest.ok && aiTest.reason && <p className="mt-1 text-xs text-rose-700">{aiTest.reason}</p>}
            <ul className="mt-2 space-y-1">
              {Object.keys(aiTest.results || {}).length === 0 && (
                <li className="text-xs text-slate-600">Kein Anbieter mit Key konfiguriert.</li>
              )}
              {Object.entries(aiTest.results || {}).map(([prov, r]) => (
                <li key={prov} data-testid={`sa-ai-test-${prov}`} className="flex items-center gap-2 text-xs">
                  {r.ok
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    : <XCircle className="h-3.5 w-3.5 text-rose-600" />}
                  <span className="font-medium uppercase text-slate-700">{prov}</span>
                  <span className="text-slate-500">{r.ok ? 'OK' : (r.error || 'Fehler')}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Zefix – Handelsregister-Lookup fürs Betriebs-Onboarding */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-int-zefix">
        <div className="mb-5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/15 text-sky-700"><Building2 className="h-4.5 w-4.5" /></div>
            <div>
              <h2 className="text-lg font-medium text-slate-900">Zefix – Handelsregister</h2>
              <p className="text-xs text-slate-500">Firmensuche im Betriebs-Onboarding (Name, UID, Adresse automatisch übernehmen).</p>
            </div>
          </div>
          {zx?.configured
            ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Aktiv</span>
            : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500"><XCircle className="h-3.5 w-3.5" /> Nicht konfiguriert</span>}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Benutzer (E-Mail)</span>
            <input data-testid="sa-zx-username" className={inputCls} placeholder="zugang@firma.ch" value={zxForm.username} onChange={(e) => setZxForm({ ...zxForm, username: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">Passwort {zx?.has_password && <span className="text-slate-500">(gesetzt)</span>}</span>
            <input data-testid="sa-zx-password" className={inputCls} type="password" placeholder={zx?.has_password ? 'Leer lassen = unverändert' : 'Passwort eingeben'} value={zxForm.password} onChange={(e) => setZxForm({ ...zxForm, password: e.target.value })} />
          </label>
        </div>
        <p className="mt-3 text-xs text-slate-400">Zugangsdaten kostenlos per E-Mail an zefix@bj.admin.ch beantragen. Ohne Zugang tragen Betriebe ihre Firmendaten manuell ein.</p>
        <div className="mt-5">
          <button data-testid="sa-save-zefix-btn" onClick={saveZx} disabled={savingZx}
            className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50">
            <Save className="h-4 w-4" /> {savingZx ? 'Speichert…' : 'Zefix speichern'}
          </button>
        </div>
      </div>

      {/* Refdata – Ärzte-/GLN-Referenzdaten (developer.refdata.ch) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-int-refdata">
        <div className="mb-5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-rose-500/15 text-rose-700"><Stethoscope className="h-4.5 w-4.5" /></div>
            <div>
              <h2 className="text-lg font-medium text-slate-900">Refdata – Ärzte &amp; GLN</h2>
              <p className="text-xs text-slate-500">Schweizer Partner-Referenzdaten für die Arztsuche in Verordnungen. Gilt für alle Mandanten; eine Praxis kann in ihren KI-Einstellungen einen eigenen Key hinterlegen.</p>
            </div>
          </div>
          {rd?.configured
            ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Aktiv</span>
            : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500"><XCircle className="h-3.5 w-3.5" /> Nicht konfiguriert</span>}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-slate-600">API-Key (Primary Key) {rd?.has_api_key && <span className="text-slate-500">(gesetzt: {rd.api_key_masked})</span>}</span>
            <input data-testid="sa-rd-apikey" className={inputCls} type="password" placeholder={rd?.has_api_key ? 'Leer lassen = unverändert' : 'Primary Key aus developer.refdata.ch'} value={rdForm.api_key} onChange={(e) => setRdForm({ ...rdForm, api_key: e.target.value })} />
          </label>
        </div>
        <p className="mt-3 text-xs text-slate-400">Key im Refdata-Developer-Portal unter „Profile" (Primary/Secondary Key). Ohne Key werden Ärzte manuell erfasst.</p>
        <div className="mt-5">
          <button data-testid="sa-save-refdata-btn" onClick={saveRd} disabled={savingRd}
            className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-rose-500 disabled:opacity-50">
            <Save className="h-4 w-4" /> {savingRd ? 'Speichert…' : 'Refdata speichern'}
          </button>
        </div>
      </div>

      {/* MediData – zentrale eLA-Anbindung (StrongFit) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-int-medidata">
        <div className="mb-5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal-500/15 text-teal-700"><FileCheck2 className="h-4.5 w-4.5" /></div>
            <div>
              <h2 className="text-lg font-medium text-slate-900">MediData – eLA-Versand (mTLS)</h2>
              <p className="text-xs text-slate-500">Zentrale Abrechnungs-Anbindung für ALLE Mandanten. Jede Praxis nutzt sie automatisch und sendet nur mit ihrer eigenen Absender-GLN. Eine Praxis mit eigenem MediData-Vertrag kann in ihren Einstellungen eigene Zertifikate hinterlegen.</p>
            </div>
          </div>
          {(md?.enabled && (md?.has_acc_cert || md?.has_prod_cert))
            ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Aktiv</span>
            : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500"><XCircle className="h-3.5 w-3.5" /> Nicht konfiguriert</span>}
        </div>

        <div className="mb-4 flex flex-wrap items-end gap-6">
          <label className="flex items-center gap-2 text-sm" data-testid="sa-md-enabled">
            <input type="checkbox" checked={mdForm.enabled} onChange={(e) => setMdForm({ ...mdForm, enabled: e.target.checked })} />
            Anbindung aktiv
          </label>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">Aktiver Zugang:</span>
            {[['acc', 'Test (ACC)'], ['prod', 'Live']].map(([v, l]) => (
              <label key={v} className="flex items-center gap-1">
                <input type="radio" name="sa-md-env" checked={(mdForm.environment || 'acc') === v} onChange={() => setMdForm({ ...mdForm, environment: v })} data-testid={`sa-md-env-${v}`} />
                {l}
              </label>
            ))}
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">Master-ID (CN)</span>
            <input data-testid="sa-md-master" className={inputCls} placeholder="z. B. 1000021525" value={mdForm.master_id} onChange={(e) => setMdForm({ ...mdForm, master_id: e.target.value })} />
          </label>
        </div>

        {[['acc', 'Test-Zugang (ACC)'], ['prod', 'Live-Zugang']].map(([pre, title]) => (
          <div key={pre} className="mb-4 rounded-xl border border-slate-200 p-4" data-testid={`sa-md-block-${pre}`}>
            <h3 className="mb-3 text-sm font-medium text-slate-800">{title}</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Basis-URL</span>
                <input data-testid={`sa-md-${pre}-url`} className={inputCls} placeholder={pre === 'acc' ? 'https://services-acc.medidata.ch/...' : 'https://...prod....medidata.ch'} value={mdForm[`${pre}_base_url`]} onChange={(e) => setMdForm({ ...mdForm, [`${pre}_base_url`]: e.target.value })} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Client-ID (X-CLIENT-ID)</span>
                <input data-testid={`sa-md-${pre}-client`} className={inputCls} placeholder="z. B. 1000021525_1200014608" value={mdForm[`${pre}_client_id`]} onChange={(e) => setMdForm({ ...mdForm, [`${pre}_client_id`]: e.target.value })} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Client-Zertifikat (PEM) {md?.[`has_${pre}_cert`] && <span className="text-emerald-600">✓ hinterlegt</span>}</span>
                <textarea data-testid={`sa-md-${pre}-cert`} className={`${inputCls} h-20 font-mono text-xs`} placeholder={md?.[`has_${pre}_cert`] ? 'Leer lassen = unverändert' : '-----BEGIN CERTIFICATE-----'} value={mdForm[`${pre}_cert_pem`]} onChange={(e) => setMdForm({ ...mdForm, [`${pre}_cert_pem`]: e.target.value })} />
                <input type="file" accept=".pem,.crt,.cer" className="mt-1 text-xs" data-testid={`sa-md-${pre}-cert-file`} onChange={(e) => readMdFile(e.target.files?.[0], `${pre}_cert_pem`)} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Privater Schlüssel (PEM) {md?.[`has_${pre}_key`] && <span className="text-emerald-600">✓ hinterlegt</span>}</span>
                <textarea data-testid={`sa-md-${pre}-key`} className={`${inputCls} h-20 font-mono text-xs`} placeholder={md?.[`has_${pre}_key`] ? 'Leer lassen = unverändert' : '-----BEGIN PRIVATE KEY-----'} value={mdForm[`${pre}_key_pem`]} onChange={(e) => setMdForm({ ...mdForm, [`${pre}_key_pem`]: e.target.value })} />
                <input type="file" accept=".pem,.key" className="mt-1 text-xs" data-testid={`sa-md-${pre}-key-file`} onChange={(e) => readMdFile(e.target.files?.[0], `${pre}_key_pem`)} />
              </label>
            </div>
          </div>
        ))}

        <p className="mb-4 text-xs text-slate-400">Zertifikat erstellen: CSR mit OpenSSL (CN = Master-ID), im MediData-Kundenportal einreichen, ausgestelltes Zertifikat + privaten Schlüssel hier hochladen. Ist „Anbindung aktiv" aus oder der aktive Zugang unvollständig, wird der Versand simuliert.</p>

        <div className="flex gap-3">
          <button data-testid="sa-save-medidata-btn" onClick={saveMd} disabled={savingMd}
            className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50">
            <Save className="h-4 w-4" /> {savingMd ? 'Speichert…' : 'MediData speichern'}
          </button>
          <button data-testid="sa-test-medidata-btn" onClick={testMd} disabled={testingMd}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {testingMd ? 'Teste…' : 'Verbindung testen'}
          </button>
        </div>
      </div>
    </div>
  );
}
